import {
  GEO_REGIONS, GEO_COUNTRIES, GEO_STATES, GEO_CITIES,
  GeoRegion,
} from '../../shared/data/geo-data';

// Mulberry32 seeded PRNG — deterministic across calls so drill-down totals stay consistent
function mulberry32(seed: number): () => number {
  let s = seed;
  return () => {
    s |= 0; s = (s + 0x6D2B79F5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const rng = mulberry32(20260409);
const rand = (min: number, max: number) => Math.floor(rng() * (max - min)) + min;

export interface CityStatEntry {
  regionCode: string;
  regionName: string;
  countryCode: string;
  countryName: string;
  stateCode: string;
  stateName: string;
  cityName: string;
  totalTrips: number;
  completedTrips: number;
  cancelledTrips: number;
  totalRevenue: number;
  totalTips: number;
  totalAirportFees: number;
  totalMiscFees: number;
  totalCityFees: number;
  driverPayout: number;
  commissionRevenue: number;
  subscriptionRevenue: number;
  totalDrivers: number;
  activeDrivers: number;
  gracePeriodDrivers: number;
  totalPassengers: number;
  activePassengers: number;
}

// ---------- Build city-level base data (generated once at import time) ----------

const CITY_STATS: CityStatEntry[] = [];

for (const city of GEO_CITIES) {
  const state = GEO_STATES.find(s => s.code === city.stateCode && s.countryCode === city.countryCode);
  const country = GEO_COUNTRIES.find(c => c.code === city.countryCode);
  const region = GEO_REGIONS.find(r => r.countries.includes(city.countryCode));

  const totalTrips = rand(50, 800);
  const completedTrips = Math.floor(totalTrips * (0.75 + rng() * 0.2));
  const totalDrivers = rand(8, 120);
  const activeDrivers = Math.floor(totalDrivers * (0.6 + rng() * 0.3));
  const gracePeriodDrivers = Math.floor((totalDrivers - activeDrivers) * (0.3 + rng() * 0.4));
  const totalPassengers = rand(30, 500);
  const activePassengers = Math.floor(totalPassengers * (0.5 + rng() * 0.4));

  const baseFare = +(totalTrips * (12 + rng() * 20)).toFixed(2);
  const totalTips = +(totalTrips * (1 + rng() * 4)).toFixed(2);
  const totalAirportFees = +(rand(2, Math.max(Math.floor(totalTrips / 5), 3)) * 5).toFixed(2);
  const totalMiscFees = +(totalTrips * (0.5 + rng() * 2)).toFixed(2);
  const totalCityFees = +(totalTrips * (0.3 + rng() * 1.5)).toFixed(2);
  const totalRevenue = +(baseFare + totalTips + totalAirportFees + totalMiscFees + totalCityFees).toFixed(2);
  const commissionRevenue = +(totalRevenue * (0.15 + rng() * 0.10)).toFixed(2);
  const driverPayout = +(totalRevenue - totalAirportFees - totalMiscFees - totalCityFees - commissionRevenue).toFixed(2);
  const subscriptionRevenue = +(rand(10, 60) * (8 + rng() * 10)).toFixed(2);

  CITY_STATS.push({
    regionCode: region?.code || '',
    regionName: region?.name || '',
    countryCode: city.countryCode,
    countryName: country?.name || city.countryCode,
    stateCode: city.stateCode,
    stateName: state?.name || city.stateCode,
    cityName: city.name,
    totalTrips,
    completedTrips,
    cancelledTrips: totalTrips - completedTrips,
    totalRevenue,
    totalTips,
    totalAirportFees,
    totalMiscFees,
    totalCityFees,
    driverPayout,
    commissionRevenue,
    subscriptionRevenue,
    totalDrivers,
    activeDrivers,
    gracePeriodDrivers,
    totalPassengers,
    activePassengers,
  });
}

// ---------- Geo filtering ----------

export interface GeoFilterParam {
  region?: string;
  country?: string;
  state?: string;
  city?: string;
}

export interface DateRange {
  from?: string;   // ISO date yyyy-MM-dd
  to?: string;     // ISO date yyyy-MM-dd
}

const TOTAL_DAYS = 365;

function dateScale(range?: DateRange): number {
  if (!range?.from || !range?.to) return 1;
  const d1 = new Date(range.from).getTime();
  const d2 = new Date(range.to).getTime();
  if (d2 <= d1) return 0;
  const days = Math.ceil((d2 - d1) / 86_400_000) + 1;
  return Math.min(1, days / TOTAL_DAYS);
}

function filterCities(geo: GeoFilterParam): CityStatEntry[] {
  let list = CITY_STATS;
  if (geo.region) {
    const r = GEO_REGIONS.find(x => x.code === geo.region);
    if (r) list = list.filter(c => r.countries.includes(c.countryCode));
  }
  if (geo.country) list = list.filter(c => c.countryCode === geo.country);
  if (geo.state) list = list.filter(c => c.stateCode === geo.state);
  if (geo.city) list = list.filter(c => c.cityName === geo.city);
  return list;
}

// ---------- Aggregation ----------

export interface StatRow {
  region: string;
  country: string;
  state: string;
  city: string;
  totalTrips: number;
  completedTrips: number;
  cancelledTrips: number;
  totalRevenue: number;
  totalTips: number;
  totalAirportFees: number;
  totalMiscFees: number;
  totalCityFees: number;
  driverPayout: number;
  commissionRevenue: number;
  subscriptionRevenue: number;
  netProfit: number;
  avgFare: number;
  totalDrivers: number;
  activeDrivers: number;
  gracePeriodDrivers: number;
  totalPassengers: number;
  activePassengers: number;
}

type KeyFn = (e: CityStatEntry) => Record<string, string>;

function aggregate(entries: CityStatEntry[], keyFn: KeyFn, scale = 1): StatRow[] {
  const groups = new Map<string, { keys: Record<string, string>; items: CityStatEntry[] }>();

  for (const e of entries) {
    const keys = keyFn(e);
    const gk = Object.values(keys).join('\0');
    let g = groups.get(gk);
    if (!g) { g = { keys, items: [] }; groups.set(gk, g); }
    g.items.push(e);
  }

  const sc = (v: number) => Math.round(v * scale);
  const scf = (v: number) => +(v * scale).toFixed(2);

  const result: StatRow[] = [];
  for (const { keys, items } of groups.values()) {
    const rawTrips = items.reduce((s, e) => s + e.totalTrips, 0);
    const rawRevenue = items.reduce((s, e) => s + e.totalRevenue, 0);
    const totalTrips = sc(rawTrips);
    const totalRevenue = scf(rawRevenue);
    const totalTips = scf(items.reduce((s, e) => s + e.totalTips, 0));
    const totalAirportFees = scf(items.reduce((s, e) => s + e.totalAirportFees, 0));
    const totalMiscFees = scf(items.reduce((s, e) => s + e.totalMiscFees, 0));
    const totalCityFees = scf(items.reduce((s, e) => s + e.totalCityFees, 0));
    const commissionRevenue = scf(items.reduce((s, e) => s + e.commissionRevenue, 0));
    const subscriptionRevenue = scf(items.reduce((s, e) => s + e.subscriptionRevenue, 0));
    // Derived so Fare Totals = Driver Payout + Fees + Commission exactly
    const driverPayout = +(totalRevenue - totalAirportFees - totalMiscFees - totalCityFees - commissionRevenue).toFixed(2);
    result.push({
      region: keys['region'] || '',
      country: keys['country'] || '',
      state: keys['state'] || '',
      city: keys['city'] || '',
      totalTrips,
      completedTrips: sc(items.reduce((s, e) => s + e.completedTrips, 0)),
      cancelledTrips: sc(items.reduce((s, e) => s + e.cancelledTrips, 0)),
      totalRevenue,
      totalTips,
      totalAirportFees,
      totalMiscFees,
      totalCityFees,
      driverPayout,
      commissionRevenue,
      subscriptionRevenue,
      netProfit: +(commissionRevenue + subscriptionRevenue).toFixed(2),
      avgFare: totalTrips ? +(totalRevenue / totalTrips).toFixed(2) : 0,
      totalDrivers: items.reduce((s, e) => s + e.totalDrivers, 0),
      activeDrivers: items.reduce((s, e) => s + e.activeDrivers, 0),
      gracePeriodDrivers: items.reduce((s, e) => s + e.gracePeriodDrivers, 0),
      totalPassengers: items.reduce((s, e) => s + e.totalPassengers, 0),
      activePassengers: items.reduce((s, e) => s + e.activePassengers, 0),
    });
  }
  return result;
}

/**
 * Returns aggregated stats rows — totals are guaranteed to be consistent:
 *   sum(cities in state) === state total
 *   sum(states in country) === country total
 *   sum(countries in region) === region total
 *   sum(all regions) === global total
 */
export function getAggregatedStats(groupBy: string, geo: GeoFilterParam, range?: DateRange): StatRow[] {
  const filtered = filterCities(geo);
  const scale = dateScale(range);
  switch (groupBy) {
    case 'region':
      return aggregate(filtered, e => ({ region: e.regionName }), scale);
    case 'country':
      return aggregate(filtered, e => ({ country: e.countryName }), scale);
    case 'state':
      return aggregate(filtered, e => ({ country: e.countryName, state: e.stateName }), scale);
    case 'city':
      return aggregate(filtered, e => ({ country: e.countryName, state: e.stateName, city: e.cityName }), scale);
    default:
      return aggregate(filtered, e => ({ region: e.regionName }), scale);
  }
}

/**
 * Revenue breakdown for the Dashboard doughnut chart.
 * Consistent with getAggregatedStats — the numbers come from the same base data.
 */
export function getRegionRevenues(geo: GeoFilterParam, range?: DateRange): { name: string; revenue: number }[] {
  const filtered = filterCities(geo);
  const scale = dateScale(range);

  if (geo.country) {
    const total = filtered.reduce((s, e) => s + e.totalRevenue, 0);
    const name = GEO_COUNTRIES.find(c => c.code === geo.country)?.name || geo.country;
    return [{ name, revenue: +(total * scale).toFixed(2) }];
  }

  if (geo.region) {
    const groups = new Map<string, number>();
    for (const e of filtered) groups.set(e.countryName, (groups.get(e.countryName) || 0) + e.totalRevenue);
    return [...groups.entries()].map(([name, rev]) => ({ name, revenue: +(rev * scale).toFixed(2) }));
  }

  const groups = new Map<string, number>();
  for (const e of filtered) groups.set(e.regionName, (groups.get(e.regionName) || 0) + e.totalRevenue);
  return [...groups.entries()].map(([name, rev]) => ({ name, revenue: +(rev * scale).toFixed(2) }));
}

/**
 * Dashboard summary totals — consistent with Stats page.
 * totalRevenue here will equal sum of region-level totalRevenue from getAggregatedStats('region', geo).
 */
export function getCityStats(): CityStatEntry[] {
  return CITY_STATS;
}

export function getDashboardTotals(geo: GeoFilterParam, range?: DateRange) {
  const filtered = filterCities(geo);
  const scale = dateScale(range);
  const sc = (v: number) => Math.round(v * scale);
  const scf = (v: number) => +(v * scale).toFixed(2);

  const totalRevenue = scf(filtered.reduce((s, e) => s + e.totalRevenue, 0));
  const totalAirportFees = scf(filtered.reduce((s, e) => s + e.totalAirportFees, 0));
  const totalMiscFees = scf(filtered.reduce((s, e) => s + e.totalMiscFees, 0));
  const totalCityFees = scf(filtered.reduce((s, e) => s + e.totalCityFees, 0));
  const commissionRevenue = scf(filtered.reduce((s, e) => s + e.commissionRevenue, 0));
  const subscriptionRevenue = scf(filtered.reduce((s, e) => s + e.subscriptionRevenue, 0));
  const totalFees = +(totalAirportFees + totalMiscFees + totalCityFees).toFixed(2);
  const driverPayout = +(totalRevenue - totalFees - commissionRevenue).toFixed(2);
  return {
    totalDrivers: filtered.reduce((s, e) => s + e.totalDrivers, 0),
    activeDrivers: filtered.reduce((s, e) => s + e.activeDrivers, 0),
    gracePeriod: filtered.reduce((s, e) => s + e.gracePeriodDrivers, 0),
    totalTrips: sc(filtered.reduce((s, e) => s + e.totalTrips, 0)),
    completedTrips: sc(filtered.reduce((s, e) => s + e.completedTrips, 0)),
    totalRevenue,
    totalFees,
    driverPayout,
    commissionRevenue,
    subscriptionRevenue,
    netProfit: +(commissionRevenue + subscriptionRevenue).toFixed(2),
    totalPassengers: filtered.reduce((s, e) => s + e.totalPassengers, 0),
    activePassengers: filtered.reduce((s, e) => s + e.activePassengers, 0),
  };
}
